import React from "react";

import { SkeletonText } from "~/components/skeleton/text";
import { Metric } from "~/models/metric";
import type { MetricQuery } from "~/models/metric-query";
import { cx } from "~/tools/class-variance";
import type { Time } from "~/tools/date-time/time";
import { formatUnits } from "~/tools/formatter-unit";
import type { JSON } from "~/tools/json";
import { RemoteData } from "~/tools/remote-data";

import { HTTPError } from "../generic/request";
import { queryInstantPromQL } from "../queries/metrics/batch";
import { type RetryValue, useQuery } from "../queries/react-query";

import { EmptyChart } from "./react/chart/chart-states/EmptyChart";
import { ErrorChart } from "./react/chart/chart-states/ErrorChart";
import { ComponentLink } from "./react/component-link/ComponentLink";

interface GaugeChartProps {
	className?: string;
	query: MetricQuery;
	unit: undefined | string;
	retry?: RetryValue<Error>;
	namespace?: JSON;
	timestamp?: Time;
}

export const GaugeChart: React.FC<GaugeChartProps> = ({ className, unit, query, timestamp, retry, namespace }) => {
	const { error, data = RemoteData.loading() } = useQuery({
		namespace,
		refetchOnTimelineSummaryUpdate: true,
		retry,
		select: (result) => result.transform((vectors) => vectors?.at(0)),

		...queryInstantPromQL({
			query: query.promql,
			step: query.step,
			timestamp,
		}),
	});

	if (error) {
		return <ErrorChart className={className} error={error} size={108} />;
	}

	return data.match({
		loading: () => <EmptyChart className={className} size={108} />,

		failure: ({ error: message }) => (
			<ErrorChart className={className} error={new HTTPError({ statusText: message })} size={108} />
		),

		success: ({ value: firstVector }) => (
			// the gauge chart should not be taller than line chart
			<div className={cx("grid max-h-50 grid-cols-1 content-center justify-items-start py-6", className)}>
				<SkeletonText className="skeleton:w-25 max-w-full font-semibold text-3xl" el="div" randomize when={firstVector}>
					{({ value }) => formatUnits(unit, value[1])}
				</SkeletonText>

				<SkeletonText
					className="skeleton:w-40 max-w-full text-13 text-gray-600 leading-6"
					el="div"
					randomize
					when={firstVector}
				>
					{(vector) => {
						const componentIdentifier = Metric.toComponentIdentifier(query.componentIdentifier, vector.metric);
						const label = Metric.toLabel(query.label, vector.metric);

						if (componentIdentifier == null) {
							return label;
						}

						return <ComponentLink componentIdentifier={componentIdentifier} text={label} />;
					}}
				</SkeletonText>
			</div>
		),
	});
};
